import type { TPillTabsProps, TPillTabsTab } from './pill-tabs.types';
import styles from './pill-tabs.module.css';
import { useCallback, useEffect, useState } from 'react';
import { isObject } from 'underscore';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

function getTabIndex<T = string | number>(tabs: TPillTabsTab<T>[], value: T | undefined) {
  return (
    tabs.findIndex((tab) => {
      if (isObject(tab)) {
        return tab.value === value;
      }

      return tab === value;
    }) || 0
  );
}

export default function PillTabs<T = string | number>({
  tabs,
  onChange,
  value,
  defaultValue,
  setHistoryState,
  historyStateKey,
}: TPillTabsProps<T>) {
  const [activeTab, setActiveTab] = useState(getTabIndex(tabs, value || defaultValue));
  const navigate = useNavigate();

  const clickTab = useCallback(
    (value: T) => () => {
      if (setHistoryState) {
        navigate('.', { state: { [historyStateKey || 'tab']: value }, replace: true, relative: 'path' });
      }
      onChange?.(value);
      const index = getTabIndex(tabs, value);
      setActiveTab(index);
    },
    [setHistoryState, onChange, tabs, navigate, historyStateKey]
  );

  const renderTab = useCallback(
    (tab: T | { value: T; label: string }, index: number) => {
      const value = !isObject(tab) ? tab : tab.value;
      const label = !isObject(tab) ? String(tab) : tab.label;

      return (
        <button
          className={classNames(styles.pillTab, {
            [styles.active]: activeTab === index,
            [styles.toRight]: activeTab === index - 1,
            [styles.toLeft]: activeTab === index + 1,
          })}
          onClick={clickTab(value)}
        >
          <span>{label}</span>
        </button>
      );
    },
    [activeTab, clickTab]
  );

  useEffect(() => {
    if (!defaultValue) {
      setActiveTab(getTabIndex(tabs, value));
    }

    if (defaultValue && value) {
      console.warn(`<PillTabs>: Both defaultValue and value props set:`, {
        defaultValue,
        value,
        stack: new Error().stack,
      });
    }
  }, [value, defaultValue, tabs]);

  return <div className={styles.container}>{tabs.map(renderTab)}</div>;
}
